/*
  Sidebar.jsx

  Meant to replicate the MUI Drawer:
  https://mui.com/material-ui/react-drawer/
*/

// Npm.
import React, { useMemo, useEffect, useRef } from 'react';

// Components.
import Divider from '../Divider';
import IconButton from '../IconButton';

// Icons.
import LeftIcon from '../../icons/ChevronLeft';
import RightIcon from '../../icons/ChevronRight';
import ClearIcon from '../../icons/Close';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    position: 'relative',
    zIndex: 1000,
    outline: 'none',  // Stops focus effect on page load when first opened.
    '&.hidden': {
      display: 'none',
    },
    '& > .content': {
      width: 256,
      height: '100%',
      backgroundColor: theme.color_paper,
      overflowX: 'hidden',
      overflowY: 'auto',
      '& > .contentInner': {
        padding: theme.space(2),
      },
    },
    '&.left > .content': {
      borderRight: `1px solid ${theme.color_divider}`,
    },
    '&.right > .content': {
      borderLeft: `1px solid ${theme.color_divider}`,
    },
    '&.temporary > .content': {
      border: 'none',
      position: 'fixed',
      top: 0,
      bottom: 0,
      zIndex: 2,
      boxShadow: theme.shadow(4),
    },
    '&.temporary.left > .content': {
      left: 0,
    },
    '&.temporary.right > .content': {
      right: 0,
    },
    '& > .backdrop': {
      position: 'fixed',
      zIndex: 1,
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
    },
  },
  navbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.space(1),
    height: 56,
    '& > .title': {
      padding: theme.space(0, 1),
      flexGrow: 1,
      fontWeight: 600,
      fontSize: '1rem',
    }
  },
}));

/**
 * Sidebar component.
 */
export default function Sidebar({
  className,
  title = '',
  position,
  variant,
  keepMounted = false,
  open = false,
  disabled = false,
  onClose,
  onCancel,
  children,
  ...rest
}) {

  const isRight = position === 'right';
  const isTemporary = variant === 'temporary';
  const usePosition = isRight ? 'right' : 'left';

  let rootClass = `Sidebar ${usePosition} ${isTemporary ? 'temporary' : 'permanent'}`;

  if (!open) {
    rootClass += ' hidden';
  }

  const classes = useStyles();
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  // Effects to close the temporary sidebar when Escape is pressed.

  useEffect(() => {
    if (open && isTemporary && onClose) {

      function handleKeyDown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };

    }
  }, [open, isTemporary, onClose]);

  // When the sidebar opens, we focus on the outside div to maintain tabbing order, and so that
  // something in the window is actually focussed so that the above "keydown" listener works.

  const rootRef = useRef();

  useEffect(() => {
    if (open && rootRef.current) {
      rootRef.current.focus();
    }
  }, [open]);

  // Don't render if closed.

  if (!keepMounted && !open) {
    return null;
  }

  function handleClickBackdrop(event) {
    event.preventDefault();
    if (onClose) {
      onClose();
    }
  }

  return (
    <div
      className={cls}
      ref={rootRef}
      tabIndex={-1}
      role="presentation"
      hidden={!open}
    >

      <div className="content" role="presentation">
        <div>
          <div className={classes.navbar}>
            {
              !isRight && Boolean(onCancel) &&
              <IconButton
                title="Cancel"
                aria-label="cancel"
                disabled={!open || disabled}
                onClick={onCancel}
              >
                <ClearIcon />
              </IconButton>
            }
            {
              !isRight &&
              <span className="title">{ title }</span>
            }
            {
              !isTemporary &&
              <IconButton
                title="Close"
                aria-label="close"
                disabled={!open || disabled}
                onClick={onClose}
              >
                { isRight ? <RightIcon /> : <LeftIcon /> }
              </IconButton>
            }
            {
              isRight &&
              <span className="title">{ title }</span>
            }
            {
              isRight && Boolean(onCancel) &&
              <IconButton
                title="Cancel"
                aria-label="cancel"
                disabled={!open || disabled}
                onClick={onCancel}
              >
                <ClearIcon />
              </IconButton>
            }
          </div>
          <Divider />
        </div>
        <div className="contentInner">

          { children }

        </div>
      </div>

      {
        isTemporary &&
        <div
          className="backdrop fadeIn"
          aria-hidden="true"
          onClick={handleClickBackdrop}
        />
      }

    </div>
  );
}
